import BusinessIcon from "@mui/icons-material/Business";
import {
  Box,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
} from "@mui/material";
import React from "react";
import { mobiliTheme } from "../../themes/mobiliTheme";

export interface BsnrTableToolbarProps {
  bsnrFilter: string;
  onBsnrFilterChange: (value: string) => void;
  /** When provided, renders a "sort by clinic" on/off switch to the right of the filter. */
  onToggleClinicSort?: (active: boolean) => void;
  /** Whether sorting by clinic is currently active. */
  clinicSortActive?: boolean;
}

/**
 * Toolbar with a BSNR/clinic filter field and an optional "Arztpraxis" sort
 * switch. The switch toggles grouping/sorting by clinic on and off (off falls
 * back to the original date sort). Rendered by the ticket overviews so it stays
 * visible even when the result list is empty (e.g. when a filter matches nothing).
 */
const BsnrTableToolbar: React.FC<BsnrTableToolbarProps> = ({
  bsnrFilter,
  onBsnrFilterChange,
  onToggleClinicSort,
  clinicSortActive = false,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 0.75,
        py: 0,
        background: mobiliTheme.cardBackground,
        borderBottom: "1px solid #e0e0e0",
      }}
    >
      <TextField
        size="small"
        variant="outlined"
        placeholder="Praxis (BSNR) suchen"
        value={bsnrFilter ?? ""}
        onChange={(e) => onBsnrFilterChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <BusinessIcon sx={{ fontSize: "1rem" }} />
            </InputAdornment>
          ),
          endAdornment: bsnrFilter ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="BSNR-Filter zurücksetzen"
                onClick={() => onBsnrFilterChange("")}
                sx={{ p: 0.25 }}
              >
                ×
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        sx={{
          maxWidth: 180,
          flex: 1,
          "& .MuiInputBase-root": { fontSize: "0.75rem" },
          "& .MuiOutlinedInput-input": { py: 0.5, px: 0.75 },
          "& .MuiInputAdornment-root": { mr: 0.25, ml: 0.25 },
        }}
      />

      {onToggleClinicSort && (
        <FormControlLabel
          sx={{ whiteSpace: "nowrap", ml: 0, mr: 0 }}
          control={
            <Switch
              size="small"
              color="secondary"
              checked={clinicSortActive}
              onChange={(e) => onToggleClinicSort(e.target.checked)}
            />
          }
          label="Nach Arztpraxis"
        />
      )}
    </Box>
  );
};

export default BsnrTableToolbar;
